/**
 * Loading Spinner Component
 * 
 * Reusable loading spinner component with multiple variants and sizes.
 * Can be used inline or as a full-page overlay.
 */

import React, { CSSProperties } from 'react';
import './LoadingSpinner.css';

// Type definitions
type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
type SpinnerVariant = 'primary' | 'secondary' | 'white' | 'gray';
type SpinnerType = 'circular' | 'dots' | 'pulse';

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  type?: SpinnerType;
  text?: string;
  overlay?: boolean;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

/**
 * Loading Spinner component
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  type = 'circular',
  text = '',
  overlay = false,
  className = '',
  style = {},
  ariaLabel = 'Loading',
  ...props
}) => {
  const baseClass = 'loading-spinner';
  const sizeClass = `loading-spinner--${size}`;
  const variantClass = `loading-spinner--${variant}`;
  const typeClass = `loading-spinner--${type}`;
  const overlayClass = overlay ? 'loading-spinner--overlay' : '';
  
  const spinnerClasses = [
    baseClass,
    sizeClass,
    variantClass,
    typeClass,
    overlayClass,
    className
  ].filter(Boolean).join(' ');

  const renderSpinner = () => {
    switch (type) {
      case 'dots':
        return (
          <div className="loading-spinner__dots">
            <div className="loading-spinner__dot"></div>
            <div className="loading-spinner__dot"></div>
            <div className="loading-spinner__dot"></div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className="loading-spinner__pulse">
            <div className="loading-spinner__pulse-ring"></div>
            <div className="loading-spinner__pulse-ring"></div>
            <div className="loading-spinner__pulse-ring"></div>
          </div>
        );
      
      case 'circular':
      default:
        return (
          <svg className="loading-spinner__circular" viewBox="0 0 50 50">
            <circle
              className="loading-spinner__path"
              cx="25"
              cy="25"
              r="20"
              fill="none"
              strokeWidth="4"
            />
          </svg>
        );
    }
  };

  return (
    <div
      className={spinnerClasses}
      style={style}
      role="status"
      aria-label={ariaLabel}
      {...props}
    >
      <div className="loading-spinner__content">
        {renderSpinner()}
        {text && (
          <div className="loading-spinner__text" aria-live="polite">
            {text}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Page Spinner Component
 * Full-page loading spinner with overlay
 */
interface PageSpinnerProps {
  text?: string;
  variant?: SpinnerVariant;
}

export const PageSpinner: React.FC<PageSpinnerProps> = ({ 
  text = 'Loading...', 
  variant = 'primary' 
}) => {
  return (
    <LoadingSpinner
      size="lg"
      variant={variant}
      text={text}
      overlay={true}
      className="page-spinner"
    />
  );
};

/**
 * Inline Spinner Component
 * Small spinner for inline use (e.g., in buttons)
 */
interface InlineSpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
}

export const InlineSpinner: React.FC<InlineSpinnerProps> = ({ 
  size = 'sm', 
  variant = 'white',
  className = ''
}) => {
  return (
    <LoadingSpinner
      size={size}
      variant={variant}
      type="circular"
      className={`inline-spinner ${className}`}
    />
  );
};

export default LoadingSpinner;
